import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const namingConventionsCode = `// 1. Intent-Revealing Names
const d = 5; //  Cryptic
const daysUntilExpiration = 5; // Semantic

// 2. Boolean Naming (Interrogative)
const active = true; //  Ambiguous
const isActive = true; // Clear
const hasPermission = false; // Clear

// 3. Verb-Noun Functions
function userData() {} //  Is it a getter? A variable?
function fetchUserRecord() {} // Explicit action`;

const solidPrinciplesCode = `// Single Responsibility Principle (SRP)
//  Bad: One class handles everything
class OrderManager {
    saveOrder() { /* DB Logic */ }
    sendInvoice() { /* Email Logic */ }
}

// Good: Decoupled responsibilities
class OrderRepository { save(order) {} }
class BillingService { sendInvoice(user) {} }

// Open/Closed Principle (OCP)
// Software entities should be open for extension but closed for modification.
class ShapeRenderer {
    render(shapes) {
        shapes.forEach(shape => shape.draw()); // Works with ANY shape that has draw()
    }
}`;

const refactoringRefinementCode = `// 1. Guard Clauses (Flattening Nesting)
function processPayment(payment) {
    if (!payment.isValid) return { error: "Invalid" };
    if (!payment.isAuthorized) return { error: "Unauthorized" };

    return executeTransaction(payment);
}

// 2. The "Parameter Object" (Handling Growth)
function createServer({
    port = 8080,
    protocol = 'http',
    timeout = 5000
} = {}) {
    // Logic...
}`;

const JSCodeQuality = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Architectural Excellence</div>
                <h1>Code Quality & Clean Code</h1>
                <p className="hero-lead">
                    In high-performance engineering, "code that works" is the baseline,
                    not the goal. Master the art of writing self-documenting,
                    maintainable, and human-centric JavaScript that scales across
                    teams and years of production.
                </p>
            </div>

            <div className="content-section">
                <h2>The Psychology of Naming</h2>
                <p>
                    Code is read far more often than it is written. Variable and
                    function names should reveal intent without requiring a manual
                    lookup of the surrounding logic. Avoid abbreviations, use
                    interrogative prefixes for booleans (like `is`, `has`, `can`),
                    and ensure functions represent the actions they perform.
                </p>
                <CodeBlock code={namingConventionsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>SOLID Principles in JavaScript</h2>
                <p>
                    The **SOLID** acronym represents five principles of object-oriented
                    design that translate perfectly to the modular world of modern JS/TS.
                    Focusing on **Single Responsibility** (SRP) ensures your modules
                    stay small and testable, while the **Open/Closed** principle (OCP)
                    allows your system to grow through extension rather than surgery.
                </p>
                <CodeBlock code={solidPrinciplesCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Refactoring for Maintainability</h2>
                <p>
                    "Arrow Code" (deeply nested `if` statements) is the primary driver
                    of technical debt. Use **Guard Clauses** to return early and flatten
                    your functions. For functions with complex configurations, prefer
                    the **Parameter Object** pattern to avoid the fragility of long
                    positional argument lists.
                </p>
                <CodeBlock code={refactoringRefinementCode} language="javascript" />
            </div>

            <ProTip><p>
                    Professional development environments rely on more than just discipline;
                    they use automated tooling. Always implement the **Boy Scout Rule**:
                    leave the code slightly cleaner than you found it. This involves
                    not just fixing logic, but updating JSDoc types, improving variable
                    names, and ensuring that ESLint/Prettier rules are strictly
                    adhered to in every commit.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Readability:** Could a junior engineer understand this function in 15 seconds?</li>
                    <li>**SRP:** Does this class or function have exactly ONE reason to change?</li>
                    <li>**Naming:** Are booleans named as questions? Are constants UPPER_SNAKE_CASE?</li>
                    <li>**Nesting:** Have you used guard clauses to keep the logical path flat?</li>
                    <li>**Documentation:** Does the code explain "Why" (for complex hacks) rather than "What"?</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSCodeQuality;


